/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import moment from 'moment';
import * as React from 'react';

import { IConditionalContainerData } from './conditional-container.data';
import {
    IConditionalContainerProps
} from './conditional-container.props.autogenerated';

//==============================================================================
/**
 * ConditionalContainer component
 * @extends {React.PureComponent<IConditionalContainerProps<IConditionalContainerData>>}
 */
//==============================================================================
class ConditionalContainer extends React.PureComponent<IConditionalContainerProps<IConditionalContainerData>> {

    //------------------------------------------------------
    //------------------------------------------------------
    public render(): JSX.Element | null {
        const { affiliations } = this.props.config;

        // If the authors have declared zero affiliations, use the other fields
        // logic to determine if the promo should be displayed
        if (!affiliations || affiliations.length === 0) {
            return this._displayPromo;
        }

        // If the authors have declared some afflitiations, check and ensure
        // that the currently logged in customer has a particular affiliation
        // declared by the authors.
        if (this._isAffiliated) {
            return this._displayPromo;
        }

        // If affiliations were declared and none matched the current employee,
        // ignore the promo and don't render
        return null;
    }

    private get _displayPromo(): JSX.Element | null {
        const { config, slots } = this.props;

        const hasSlots = slots && slots.contentBlock && slots.contentBlock.length;
        if (hasSlots) {

            // Ensure we should be showing our child modules
            const isEnabled = (config.enabled && this._isEnabled(config.startDate, config.endDate));

            return (
                <React.Fragment>
                    {isEnabled &&
                        this._renderSlotItems(slots.contentBlock)
                    }
                </React.Fragment>
            );
        }

        // No slots, so there's nothing to do
        return null;
    }

    //==============================================================================
    // TSX COMPONENTS
    //==============================================================================

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderSlotItems(items: React.ReactNode[]): JSX.Element {
        return (
            <React.Fragment>
                {items.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    //==============================================================================
    // PRIVATE MEMBERS
    //==============================================================================

    //------------------------------------------------------
    // Determine whether the current date is between the start
    // and end dates.
    //
    // Start and end dates are optional, so only check if they
    // are present and valid.
    //------------------------------------------------------
    private _isEnabled(startDate: string | undefined, endDate: string | undefined): boolean {
        const now = moment();

        // Check to see if we should disable based on the start date
        if (startDate) {
            const startDateMoment = moment(startDate, 'MM/DD/YYYY');
            if (startDateMoment.isValid() && now.isBefore(startDateMoment, 'day')) {
                return false;
            }
        }

        // Check to see if we should disable based on the end date
        if (endDate) {
            const endDateMoment = moment(endDate, 'MM/DD/YYYY');
            if (endDateMoment.isValid() && now.isAfter(endDateMoment, 'day')) {
                return false;
            }
        }

        // We failed to fail out. Success!
        return true;
    }

    private get _isAffiliated(): boolean {
        const { affiliations } = this.props.config;
        const customer = this.props.data.customer.result;

        // we need to make sure that affiliations were loaded before continuing
        if (customer && customer.CustomerAffiliations) {
            for (const declaredAffiliation of affiliations!) {
                const affiliationExists =
                    customer.CustomerAffiliations.find(affiliation => String(affiliation.RetailAffiliationId) === declaredAffiliation.id);
                if (affiliationExists) { return true; }
            }
        }
        return false;
    }
}

export default ConditionalContainer;